/**
 * Fighting-game character physics. ARCHITECTURE.md §4 (`src/physics`).
 *
 * This system is a SERVICE, not a ticker: it deliberately has no `fixedUpdate`. `combat-sim`
 * calls its phases in an explicit order inside a single tick, because "integrate, then build
 * volumes, then resolve hits, then separate" is an ordering that must not be at the mercy of
 * a dependency graph. Combat reaches it via `ctx.systems.get('physics')` — no import, so the
 * three-free rule holds in both directions.
 *
 * Deliberately NOT a rigid-body simulation: positions are integrated from authored velocities,
 * knockback is scripted, and separation is a hard constraint. Uncontrolled dynamics would make
 * juggle heights and wall carries non-reproducible.
 */

import {
  GRAVITY, GROUND_Y, STAGE_HALF_WIDTH, STAGE_HALF_DEPTH, WALL_SPLAT_SPEED, JUGGLE_CEILING,
  vec3, clamp, signNonZero, System,
} from '../core/index.js';
import {
  createCapsule, buildHurtboxes, buildPushbox, buildHitbox, buildThrowbox,
  HURTBOX_COUNT, levelHurtMask,
} from './boxes.js';
import { capsulesOverlap, capsuleSweepOverlap, pushboxOverlapXZ, forwardReach } from './collide.js';

/** Max hitboxes a single move may declare (mirrors combat/fighter-state MAX_HITBOXES). */
const MAX_HITBOXES = 8;
/** Velocity below which a fighter is considered at rest, m/s. */
const REST_SPEED = 0.02;
/** Ground friction while grounded and not being driven by a move, 1/s. */
const GROUND_DRAG = 9.0;
/** Air drag on the combat axis during juggles, 1/s. Low, so juggle carries stay predictable. */
const AIR_DRAG = 0.6;

export class PhysicsSystem extends System {
  static id = 'physics';
  static deps = [];
  static budgetMs = 0.35;

  constructor() {
    super();
    /** Per-fighter volume sets, allocated once at init. */
    this.volumes = [];
    /** Previous-tick hitbox poses, for swept tests. */
    this.prevHitboxes = [];
    this._normal = vec3();
    this._contact = { x: 0, y: 0, z: 0, nx: 0, ny: 0, nz: 0, depth: 0 };
    /** Wall contact results for the current tick, read by combat. */
    this.wallEvents = [
      { hit: false, side: 0, speed: 0 },
      { hit: false, side: 0, speed: 0 },
    ];
    this.landEvents = [
      { landed: false, speed: 0, hard: false },
      { landed: false, speed: 0, hard: false },
    ];
    this.stageHalfWidth = STAGE_HALF_WIDTH;
    this.stageHalfDepth = STAGE_HALF_DEPTH;
    /** Diagnostic: how often the juggle ceiling backstop fired. Should always be 0. */
    this.ceilingHits = 0;
  }

  async init(ctx) {
    for (let i = 0; i < 2; i++) {
      const hurt = new Array(HURTBOX_COUNT);
      for (let h = 0; h < HURTBOX_COUNT; h++) hurt[h] = createCapsule();
      const hit = new Array(MAX_HITBOXES);
      const prevHit = new Array(MAX_HITBOXES);
      for (let h = 0; h < MAX_HITBOXES; h++) {
        hit[h] = createCapsule();
        prevHit[h] = createCapsule();
      }
      this.volumes.push({
        hurt,
        push: createCapsule(),
        hit,
        hitCount: 0,
        throwBox: createCapsule(),
        throwActive: false,
      });
      this.prevHitboxes.push(prevHit);
    }
    ctx.log.debug('physics: volumes allocated for 2 fighters');
  }

  // -------------------------------------------------------------------------
  // Phase 1 — integrate
  // -------------------------------------------------------------------------

  /**
   * Integrates velocities into positions, applies gravity, resolves the ground plane and the
   * stage boundary. Called by combat-sim once per tick, after intents are set.
   * @param {number} step seconds
   */
  integrate(step, ctx) {
    const fighters = ctx.sim.fighters;
    for (let i = 0; i < fighters.length; i++) {
      const f = fighters[i];
      const wall = this.wallEvents[i];
      const land = this.landEvents[i];
      wall.hit = false;
      wall.side = 0;
      wall.speed = 0;
      land.landed = false;
      land.speed = 0;
      land.hard = false;

      // Hitstop freezes motion entirely — the impact reads as a collision, not two
      // independent animations sliding past each other.
      if (f.frozenTicks > 0) continue;

      // Gravity is decided by POSITION AND VELOCITY, not by the airborne state classification.
      // Several moves (Kestrel's Riser, jump-cancel hops) give upward velocity while the fighter
      // is still in a grounded state; gating gravity on `f.airborne` left them rising forever.
      const offGround = f.y > GROUND_Y + 1e-5 || f.vy > 0;
      if (offGround) {
        f.vy += GRAVITY * f.juggleGravityMul * step;
        // Mild air drag keeps long juggle carries from drifting further each patch.
        f.vx -= f.vx * Math.min(1, AIR_DRAG * step);
      } else {
        // Grounded drag applies only to residual knockback; move-driven velocity is
        // rewritten every tick by the state machine, so this cannot fight it.
        if (!f.attacking) {
          const drag = Math.min(1, GROUND_DRAG * step);
          f.vx -= f.vx * drag;
          f.vz -= f.vz * drag;
        }
        if (Math.abs(f.vx) < REST_SPEED) f.vx = 0;
        if (Math.abs(f.vz) < REST_SPEED) f.vz = 0;
      }

      f.x += f.vx * step;
      f.y += f.vy * step;
      f.z += f.vz * step;

      // Juggle ceiling: a hard backstop, not a tuning value. Legitimate juggles top out around
      // 2 m, so hitting this means a data error or an unforeseen interaction is carrying the
      // fighter off-screen. Clamp rather than let them leave the stage.
      if (f.y > JUGGLE_CEILING) {
        f.y = JUGGLE_CEILING;
        if (f.vy > 0) f.vy = 0;
        this.ceilingHits++;
      }

      // --- ground plane ---
      if (f.y <= GROUND_Y) {
        if (offGround || f.airborne || f.vy < 0) {
          land.landed = f.y < GROUND_Y || f.vy < -0.05;
          land.speed = -f.vy;
          land.hard = -f.vy > 6.5;
        }
        f.y = GROUND_Y;
        if (f.vy < 0) f.vy = 0;
      }

      // --- stage boundary on the combat axis ---
      const limit = this.stageHalfWidth - f.stats.pushboxRadius;
      if (f.x > limit) {
        wall.hit = true;
        wall.side = 1;
        wall.speed = Math.abs(f.vx);
        f.x = limit;
        if (f.vx > 0) f.vx = 0;
      } else if (f.x < -limit) {
        wall.hit = true;
        wall.side = -1;
        wall.speed = Math.abs(f.vx);
        f.x = -limit;
        if (f.vx < 0) f.vx = 0;
      }
      f.wallSide = wall.hit ? wall.side : this._nearWall(f) ;

      // --- depth clamp (sidestep lane) ---
      const dLimit = this.stageHalfDepth - f.stats.pushboxRadius * 0.5;
      if (f.z > dLimit) {
        f.z = dLimit;
        if (f.vz > 0) f.vz = 0;
      } else if (f.z < -dLimit) {
        f.z = -dLimit;
        if (f.vz < 0) f.vz = 0;
      }
    }
  }

  /** +1/-1 when the fighter is within a body's width of a wall, else 0. Drives wall pressure. */
  _nearWall(f) {
    const limit = this.stageHalfWidth - f.stats.pushboxRadius;
    const margin = f.stats.pushboxRadius * 2.2;
    if (f.x > limit - margin) return 1;
    if (f.x < -limit + margin) return -1;
    return 0;
  }

  /** True when the fighter is close enough to a wall for wall-splat rules to apply. */
  isCornered(f) {
    return this._nearWall(f) !== 0;
  }

  /**
   * Would this impact speed produce a wall splat rather than a bump?
   * Lighter fighters splat more readily, which is why Kestrel fears the corner.
   */
  isWallSplatSpeed(f, speed) {
    return speed >= WALL_SPLAT_SPEED * f.stats.wallSplatThresholdMul;
  }

  // -------------------------------------------------------------------------
  // Phase 2 — build volumes
  // -------------------------------------------------------------------------

  /** Rebuilds hurtboxes and pushboxes for both fighters at their final positions. */
  buildDefensiveVolumes(ctx) {
    const fighters = ctx.sim.fighters;
    for (let i = 0; i < fighters.length; i++) {
      const f = fighters[i];
      const v = this.volumes[i];
      buildHurtboxes(f, v.hurt);
      buildPushbox(f, v.push);
    }
  }

  /**
   * Rebuilds the attacker's active hitboxes for this tick, keeping the previous tick's poses
   * for swept tests. Only boxes whose authored window contains `moveTick` become active.
   */
  buildOffensiveVolumes(ctx) {
    const fighters = ctx.sim.fighters;
    for (let i = 0; i < fighters.length; i++) {
      const f = fighters[i];
      const v = this.volumes[i];
      const prev = this.prevHitboxes[i];

      // Retain last tick's poses before overwriting.
      for (let h = 0; h < v.hitCount; h++) copyCapsule(prev[h], v.hit[h]);
      const prevCount = v.hitCount;
      v.hitCount = 0;
      v.throwActive = false;

      const move = f.move;
      if (!move || f.frozenTicks > 0) {
        // Nothing active: clear stale prev poses so a re-activation cannot sweep from
        // wherever the limb happened to be several moves ago.
        if (prevCount > 0) for (let h = 0; h < prevCount; h++) prev[h].active = false;
        continue;
      }

      const t = f.moveTick;
      if (move.throwBox && t >= move.throwBox.tFrom && t < move.throwBox.tTo) {
        buildThrowbox(f, move.throwBox, v.throwBox);
        v.throwActive = true;
      }
      const boxes = move.hitboxes;
      for (let b = 0; b < boxes.length && v.hitCount < MAX_HITBOXES; b++) {
        const hb = boxes[b];
        if (t < hb.tFrom || t >= hb.tTo) continue;
        const out = v.hit[v.hitCount];
        buildHitbox(f, hb, out);
        out.level = move.level;
        // Remember which authored box this is, so once-per-box hit flags stay correct.
        out.sourceIndex = b;
        // First tick of activation has no meaningful previous pose: seed it to itself so the
        // sweep degenerates to a discrete test instead of sweeping from the origin.
        if (v.hitCount >= prevCount || !prev[v.hitCount].active) copyCapsule(prev[v.hitCount], out);
        v.hitCount++;
      }
    }
  }

  // -------------------------------------------------------------------------
  // Phase 3 — queries used by combat's hit resolution
  // -------------------------------------------------------------------------

  /**
   * Tests every active hitbox of `attackerIndex` against the hurtboxes of `defenderIndex`.
   * Respects hit level vs. posture (a high whiffs over a crouch; a low only touches legs).
   *
   * @returns {object|null} `{ hitboxIndex, sourceIndex, region, contact }` or null
   */
  queryHit(ctx, attackerIndex, defenderIndex) {
    const attacker = ctx.sim.fighters[attackerIndex];
    const defender = ctx.sim.fighters[defenderIndex];
    const av = this.volumes[attackerIndex];
    const dv = this.volumes[defenderIndex];
    if (av.hitCount === 0) return null;

    const move = attacker.move;
    if (!move) return null;
    if (defender.invulnTicks > 0) return null;
    if (defender.invulnLevels.length > 0 && defender.invulnLevels.includes(move.level)) return null;
    if (defender.airborne && !move.hitsAir) return null;
    if (defender.down && !move.hitsGrounded) return null;
    if (move.juggleOnly && !defender.juggleable) return null;

    const mask = levelHurtMask(move.level, defender.crouching);
    if (mask === 0) return null;

    const prev = this.prevHitboxes[attackerIndex];
    let best = null;
    let bestPriority = -1;

    for (let h = 0; h < av.hitCount; h++) {
      const box = av.hit[h];
      const src = box.sourceIndex;
      if (attacker.hitboxUsed[src]) continue;
      for (let r = 0; r < HURTBOX_COUNT; r++) {
        if ((mask & (1 << r)) === 0) continue;
        const hurt = dv.hurt[r];
        if (!capsuleSweepOverlap(prev[h], box, hurt, this._contact)) continue;
        if (box.priority > bestPriority) {
          bestPriority = box.priority;
          best = best || { hitboxIndex: 0, sourceIndex: 0, region: 0, contact: this._contact };
          best.hitboxIndex = h;
          best.sourceIndex = src;
          best.region = r;
          best.contact = this._contact;
        }
        break; // one region per box is enough; regions are ordered legs→head
      }
    }
    return best;
  }

  /**
   * Tests the attacker's throw box against the defender's pushbox.
   * @returns {boolean}
   */
  queryThrow(ctx, attackerIndex, defenderIndex) {
    const attacker = ctx.sim.fighters[attackerIndex];
    const defender = ctx.sim.fighters[defenderIndex];
    const av = this.volumes[attackerIndex];
    const dv = this.volumes[defenderIndex];
    if (!av.throwActive) return false;
    if (!defender.throwable) return false;

    const catches = attacker.move.throwBox.catches;
    if (catches === 'stand' && defender.crouching) return false;
    if (catches === 'crouch' && !defender.crouching) return false;

    return capsulesOverlap(av.throwBox, dv.push, this._contact);
  }

  /** The contact record from the most recent successful query. Do not retain. */
  get lastContact() {
    return this._contact;
  }

  // -------------------------------------------------------------------------
  // Phase 4 — separation and facing
  // -------------------------------------------------------------------------

  /**
   * Hard pushbox separation. Both fighters share the correction unless one is cornered,
   * in which case the free fighter absorbs all of it — that asymmetry is what makes wall
   * pressure a real resource rather than a cosmetic detail.
   */
  separate(ctx) {
    const [a, b] = ctx.sim.fighters;
    // A grappled pair is intentionally interpenetrating; separating them would tear the
    // throw animation apart.
    if (a.throwPartner >= 0 || b.throwPartner >= 0) return;
    if (a.state === 'finisher-attacker' || b.state === 'finisher-attacker') return;

    const va = this.volumes[0];
    const vb = this.volumes[1];
    buildPushbox(a, va.push);
    buildPushbox(b, vb.push);

    const depth = pushboxOverlapXZ(va.push, vb.push, this._normal);
    if (depth <= 0) return;

    const aCornered = this._cornerBlocked(a, -this._normal.x);
    const bCornered = this._cornerBlocked(b, this._normal.x);

    let shareA = 0.5;
    let shareB = 0.5;
    if (aCornered && !bCornered) {
      shareA = 0;
      shareB = 1;
    } else if (bCornered && !aCornered) {
      shareA = 1;
      shareB = 0;
    } else if (aCornered && bCornered) {
      // Both walled (a very narrow stage or a mid-air stack): split and accept the overlap.
      shareA = 0.5;
      shareB = 0.5;
    } else {
      // Heavier fighters give less ground. Bulwark walks Kestrel backwards by existing.
      const total = a.stats.mass + b.stats.mass;
      shareA = b.stats.mass / total;
      shareB = a.stats.mass / total;
    }

    const nx = this._normal.x;
    const nz = this._normal.z;
    a.x -= nx * depth * shareA;
    a.z -= nz * depth * shareA;
    b.x += nx * depth * shareB;
    b.z += nz * depth * shareB;

    this._clampToStage(a);
    this._clampToStage(b);
  }

  /** True when pushing this fighter along `dirX` would drive them into a wall. */
  _cornerBlocked(f, dirX) {
    const limit = this.stageHalfWidth - f.stats.pushboxRadius;
    if (dirX > 0) return f.x >= limit - 0.02;
    if (dirX < 0) return f.x <= -limit + 0.02;
    return false;
  }

  _clampToStage(f) {
    const limit = this.stageHalfWidth - f.stats.pushboxRadius;
    f.x = clamp(f.x, -limit, limit);
    const dLimit = this.stageHalfDepth - f.stats.pushboxRadius * 0.5;
    f.z = clamp(f.z, -dLimit, dLimit);
  }

  /**
   * Consistent facing correction. A fighter only re-faces when they are actually free to turn:
   * mid-attack and mid-hitstun facing is frozen, otherwise crossing up would teleport a
   * limb through the opponent.
   */
  correctFacing(ctx) {
    const fighters = ctx.sim.fighters;
    const [a, b] = fighters;
    for (let i = 0; i < 2; i++) {
      const f = fighters[i];
      const other = i === 0 ? b : a;
      if (f.attacking || f.inHitstun || f.frozenTicks > 0 || f.throwPartner >= 0) continue;
      if (f.down || f.state === 'knockdown-fall') continue;
      const dx = other.x - f.x;
      if (Math.abs(dx) < 0.04) continue; // dead zone stops jitter at exact overlap
      f.facing = signNonZero(dx);
    }
  }

  // -------------------------------------------------------------------------
  // Queries for AI, UI and the debug overlay
  // -------------------------------------------------------------------------

  /** Horizontal gap between the two pushbox surfaces, in metres. Negative means overlapping. */
  gap(ctx) {
    const [a, b] = ctx.sim.fighters;
    return Math.abs(b.x - a.x) - (a.stats.pushboxRadius + b.stats.pushboxRadius);
  }

  /** Reach of a move's furthest hitbox, in metres from the attacker's origin. */
  moveReach(f, move) {
    let best = 0;
    for (const hb of move.hitboxes) {
      const a = hb.ox + hb.r;
      const b = hb.ex + hb.r;
      const reach = Math.max(a, b);
      if (reach > best) best = reach;
    }
    if (move.throwBox) best = Math.max(best, move.throwBox.ox + move.throwBox.r);
    return best;
  }

  /** Live reach of whatever the fighter is currently doing. Debug overlay only. */
  currentReach(index) {
    const v = this.volumes[index];
    let best = 0;
    for (let h = 0; h < v.hitCount; h++) {
      const r = forwardReach(v.hit[h], 0, 1);
      if (r > best) best = r;
    }
    return best;
  }

  /** Volume set for the debug overlay. Read-only; do not retain the capsule objects. */
  volumesFor(index) {
    return this.volumes[index];
  }

  dispose() {
    this.volumes.length = 0;
    this.prevHitboxes.length = 0;
  }
}

function copyCapsule(dst, src) {
  dst.ax = src.ax; dst.ay = src.ay; dst.az = src.az;
  dst.bx = src.bx; dst.by = src.by; dst.bz = src.bz;
  dst.r = src.r;
  dst.kind = src.kind;
  dst.owner = src.owner;
  dst.active = src.active;
  dst.level = src.level;
  dst.priority = src.priority;
  dst.joint = src.joint;
  dst.sourceIndex = src.sourceIndex;
  return dst;
}
